import type {
  EndpointEventKpi,
  EndpointEventRow,
  EventSeverityStat,
} from '../endpoints/details/endpointDetailsTypes';
import type { EndpointsState } from '../endpoints/useEndpointsData';
import type {
  WazuhEndpointCriticalVulnerabilitiesData,
  WazuhEndpointFailedComplianceData,
  WazuhEndpointHealthData,
  WazuhEndpointMetadataData,
  WazuhEndpointSecurityOverviewData,
  WazuhEndpointSecurityScoreData,
  WazuhEndpointSnapshotData,
  WazuhEndpointItem,
} from 'src/types/mindtime/wazuhEndpoints';
import { getAgentCompliancePreview } from './compliance';
import { previewPaged } from './previewUtils';
import { getAgentVulnerabilitiesPreview } from './vulnerabilities';
import { formatEndpointLabel } from '../shared/endpointLabel';

export const ENDPOINT_EVENTS_PREVIEW_KPIS: EndpointEventKpi[] = [
  { id: 'total', label: 'Total Events', value: 128, trend: '12%', trendDirection: 'up' },
  { id: 'critical', label: 'Critical Events', value: 6, trend: '3%', trendDirection: 'up' },
  { id: 'security', label: 'Security Events', value: 42, trend: '8%', trendDirection: 'up' },
  { id: 'informational', label: 'Informational Events', value: 80, trend: '5%', trendDirection: 'up' },
];

export const ENDPOINT_EVENTS_PREVIEW_SEVERITY: EventSeverityStat[] = [
  { id: 'critical', label: 'Critical', value: 6, trend: '3%' },
  { id: 'high', label: 'High', value: 18, trend: '6%' },
  { id: 'low', label: 'Low', value: 104, trend: '9%' },
];

export const ENDPOINT_EVENTS_PREVIEW_ROWS: EndpointEventRow[] = [
  {
    id: 'preview-event-1',
    name: 'Suspicious PowerShell Execution',
    category: 'Execution',
    severity: 'high',
    user: 'SYSTEM',
    source: 'FIN-SRV-02',
    time: '2026-08-28T10:15:00.000Z',
    status: 'open',
  },
  {
    id: 'preview-event-2',
    name: 'Failed Login Attempt',
    category: 'Authentication',
    severity: 'medium',
    user: 'admin',
    source: '10.0.12.45',
    time: '2026-08-28T09:42:00.000Z',
    status: 'in_progress',
  },
  {
    id: 'preview-event-3',
    name: 'Malware Signature Match',
    category: 'Malware',
    severity: 'critical',
    user: 'SYSTEM',
    source: 'HR-PC-17',
    time: '2026-08-28T08:30:00.000Z',
    status: 'open',
  },
  {
    id: 'preview-event-4',
    name: 'Registry Key Modified',
    category: 'Persistence',
    severity: 'low',
    user: 'svc_backup',
    source: 'APP-SERVER-01',
    time: '2026-08-28T07:55:00.000Z',
    status: 'resolved',
  },
];

export const PREVIEW_ENDPOINT_IDS = ['preview-endpoint-1', 'preview-endpoint-2', 'preview-endpoint-3'] as const;

const PREVIEW_ENDPOINT_ROWS: WazuhEndpointItem[] = [
  {
    id: 'preview-endpoint-1',
    systemWazuhAgentId: 'preview-endpoint-1',
    wazuhAgentId: '001',
    endpoint: 'FIN-SRV-02',
    name: 'FIN-SRV-02',
    hostName: 'fin-srv-02.acme.local',
    organization: 'Acme Financial',
    organizationId: 'preview-org-1',
    status: 'Healthy',
    agentStatus: 'Active',
    riskLevel: 'Low',
    os: 'Windows Server 2022',
    system: 'windows',
    vulnerabilities: 3,
    alerts: 2,
    criticalAlerts: 0,
    lastSeen: '2026-08-28T10:15:00.000Z',
    ip: '10.0.12.45',
  },
  {
    id: 'preview-endpoint-2',
    systemWazuhAgentId: 'preview-endpoint-2',
    wazuhAgentId: '002',
    endpoint: 'HR-PC-17',
    name: 'HR-PC-17',
    hostName: 'hr-pc-17.vertex.local',
    organization: 'Vertex Solutions',
    organizationId: 'preview-org-2',
    status: 'Warning',
    agentStatus: 'Active',
    riskLevel: 'Medium',
    os: 'Windows 11',
    system: 'windows',
    vulnerabilities: 8,
    alerts: 5,
    criticalAlerts: 1,
    lastSeen: '2026-08-28T09:42:00.000Z',
    ip: '10.0.18.22',
  },
  {
    id: 'preview-endpoint-3',
    systemWazuhAgentId: 'preview-endpoint-3',
    wazuhAgentId: '003',
    endpoint: 'APP-SERVER-01',
    name: 'APP-SERVER-01',
    hostName: 'app-server-01.nexus.local',
    organization: 'Nexus Dynamics',
    organizationId: 'preview-org-3',
    status: 'Critical',
    agentStatus: 'Active',
    riskLevel: 'High',
    os: 'Ubuntu 22.04',
    system: 'linux',
    vulnerabilities: 14,
    alerts: 12,
    criticalAlerts: 3,
    lastSeen: '2026-08-28T08:30:00.000Z',
    ip: '10.0.22.8',
  },
  {
    id: 'preview-endpoint-4',
    systemWazuhAgentId: 'preview-endpoint-4',
    wazuhAgentId: '004',
    endpoint: 'DB-SERVER-04',
    name: 'DB-SERVER-04',
    hostName: 'db-server-04.pinnacle.local',
    organization: 'Pinnacle Group',
    organizationId: 'preview-org-4',
    status: 'Offline',
    agentStatus: 'Disconnected',
    riskLevel: 'High',
    os: 'RHEL 9',
    system: 'linux',
    vulnerabilities: 6,
    alerts: 0,
    criticalAlerts: 0,
    lastSeen: '2026-08-27T22:10:00.000Z',
    ip: '10.0.30.15',
  },
  {
    id: 'preview-endpoint-5',
    systemWazuhAgentId: 'preview-endpoint-5',
    wazuhAgentId: '005',
    endpoint: 'WEB-APP-07',
    name: 'WEB-APP-07',
    hostName: 'web-app-07.acme.local',
    organization: 'Acme Financial',
    organizationId: 'preview-org-1',
    status: 'Healthy',
    agentStatus: 'Active',
    riskLevel: 'Low',
    os: 'Debian 12',
    system: 'linux',
    vulnerabilities: 2,
    alerts: 1,
    criticalAlerts: 0,
    lastSeen: '2026-08-28T11:05:00.000Z',
    ip: '10.0.12.88',
  },
];

export const getEndpointsPreviewState = (): EndpointsState => ({
  loading: false,
  summary: {
    total: 24,
    healthy: 18,
    requiringAttention: 4,
    warning: 3,
    critical: 2,
    offline: 1,
  },
  items: previewPaged(PREVIEW_ENDPOINT_ROWS, 0, 15),
});

export const getEndpointPreviewRow = (systemWazuhAgentId: string): WazuhEndpointItem | null =>
  PREVIEW_ENDPOINT_ROWS.find(
    (row) => row.systemWazuhAgentId === systemWazuhAgentId || row.id === systemWazuhAgentId,
  ) ?? PREVIEW_ENDPOINT_ROWS[0] ?? null;

export const getPreviewEndpointFilterOptions = () =>
  PREVIEW_ENDPOINT_ROWS.map((row) => {
    const endpoint = row.endpoint ?? row.name ?? '';
    const hostName = row.hostName?.trim();
    return {
      id: row.systemWazuhAgentId ?? row.id ?? '',
      label: hostName ? `${endpoint} (${formatEndpointLabel(hostName)})` : endpoint,
    };
  });

export const getPreviewClientFilterOptions = () => [
  { id: 'preview-org-1', label: 'Acme Financial' },
  { id: 'preview-org-2', label: 'Vertex Solutions' },
  { id: 'preview-org-3', label: 'Nexus Dynamics' },
  { id: 'preview-org-4', label: 'Pinnacle Group' },
];

export const ENDPOINT_PREVIEW_INSTALL_COMMANDS = [
  'curl -so wazuh-agent.msi https://packages.wazuh.com/4.x/windows/wazuh-agent-4.7.5-1.msi',
  'msiexec /i wazuh-agent.msi /q WAZUH_MANAGER="manager.example.com" WAZUH_AGENT_NAME="preview-agent"',
];

export type EndpointDashboardPreviewBundle = {
  snapshot: WazuhEndpointSnapshotData;
  metadata: WazuhEndpointMetadataData;
  securityScore: WazuhEndpointSecurityScoreData;
  health: WazuhEndpointHealthData;
  securityOverview: {
    d7: WazuhEndpointSecurityOverviewData;
    d30: WazuhEndpointSecurityOverviewData;
  };
  failedCompliance: WazuhEndpointFailedComplianceData;
  criticalVulns: WazuhEndpointCriticalVulnerabilitiesData;
};

export const getEndpointDashboardPreviewBundle = (
  systemWazuhAgentId: string,
): EndpointDashboardPreviewBundle | null => {
  const row = getEndpointPreviewRow(systemWazuhAgentId);
  if (!row) return null;

  const id = row.systemWazuhAgentId ?? row.id ?? systemWazuhAgentId;
  const endpointLabel = row.endpoint ?? row.name ?? id;
  const alertsTotal = row.alerts ?? 5;
  const vulnsTotal = row.vulnerabilities ?? 8;
  const criticalAlerts = row.criticalAlerts ?? 1;
  const syncedAt = row.lastSeen ?? '2026-08-28T10:00:00.000Z';
  const compliancePreview = getAgentCompliancePreview(endpointLabel);
  const vulnPreview = getAgentVulnerabilitiesPreview(id);

  return {
    snapshot: {
      systemWazuhAgentId: id,
      risk: (row.riskLevel ?? row.risk ?? 'medium').toLowerCase(),
      overallScore: 74,
      alerts: {
        total: alertsTotal,
        critical: criticalAlerts,
        high: Math.max(1, Math.floor(alertsTotal / 3)),
        medium: Math.max(0, alertsTotal - criticalAlerts - 1),
      },
      vulnerabilities: {
        total: vulnsTotal,
        critical: 2,
        high: 3,
        medium: Math.max(0, vulnsTotal - 5),
      },
      meta: { lastSyncedAt: syncedAt },
    },
    metadata: {
      systemWazuhAgentId: id,
      wazuhAgentId: row.wazuhAgentId,
      hostname: endpointLabel,
      status: row.agentStatus ?? row.status ?? 'Active',
      os: row.os,
      ip: row.ip,
      agentVersion: '4.7.5',
      lastSeen: row.lastSeen,
      meta: { lastSyncedAt: syncedAt },
    },
    securityScore: {
      systemWazuhAgentId: id,
      score: 76.5,
      impacts: [
        { factor: 'Vulnerabilities', impactPercent: -12 },
        { factor: 'Compliance', impactPercent: -8 },
        { factor: 'Alerts', impactPercent: -3 },
      ],
      meta: { lastSyncedAt: syncedAt },
    },
    health: {
      systemWazuhAgentId: id,
      overallHealthScore: 78,
      pillars: [
        { key: 'agent', label: 'Agent', score: 92 },
        { key: 'vulnerabilities', label: 'Vulnerabilities', score: 68 },
        { key: 'compliance', label: 'Compliance', score: 74 },
      ],
      meta: { lastSyncedAt: syncedAt },
    },
    securityOverview: {
      d7: {
        systemWazuhAgentId: id,
        period: '7d',
        openVulnerabilities: vulnsTotal,
        scaChecksFailed: compliancePreview.summary.failedControls,
        fileIntegrityChanges: 2,
        threatsDetected: criticalAlerts,
        meta: { lastSyncedAt: syncedAt },
      },
      d30: {
        systemWazuhAgentId: id,
        period: '30d',
        openVulnerabilities: vulnsTotal + 2,
        scaChecksFailed: compliancePreview.summary.failedControls + 1,
        fileIntegrityChanges: 5,
        threatsDetected: alertsTotal,
        meta: { lastSyncedAt: syncedAt },
      },
    },
    failedCompliance: previewPaged(
      compliancePreview.items
        .filter((item) => item.status === 'failed')
        .map((item) => ({
          id: item.id,
          checkId: item.checkId,
          title: item.title,
          framework: item.framework,
          category: item.category,
          systemWazuhAgentId: id,
          wazuhAgentId: row.wazuhAgentId,
          lastChecked: item.lastChecked,
        })),
      0,
      5,
    ),
    criticalVulns: previewPaged(
      vulnPreview.items
        .filter((item) => item.severity === 'critical' || item.severity === 'high')
        .map((item) => ({
          id: item.id,
          cveId: item.cveId,
          title: item.title,
          severity: item.severity,
          cvssScore: item.cvssScore,
          status: item.status,
          packageName: item.packageName,
          systemWazuhAgentId: id,
          detectedAt: item.detectedAt,
        })),
      0,
      5,
    ),
  };
};
